<template>
  <div class="wechat-browser-guide">
    <svg v-if="meetingLink" class="browser-guide-arrow" viewBox="0 0 120 110" aria-hidden="true">
      <path d="M8 103C36 86 44 38 106 14" />
      <path d="M84 8L108 13L99 36" />
    </svg>
    <div class="browser-guide-copy">
      <template v-if="meetingLink">
        <span class="browser-guide-menu" aria-hidden="true">•••</span>
        <h1>{{ t('message.wechatBrowserTitle') }}</h1>
        <p>{{ t('message.wechatBrowserHint') }}</p>
        <button class="copy-link-button" type="button" :disabled="copying" :aria-busy="copying" @click="copyLink">
          {{ copying ? t('message.copyingMeetingLink') : t('button.copyMeetingLink') }}
        </button>
        <p v-if="copyMessage" role="status">{{ copyMessage }}</p>
        <input v-if="manualCopy" ref="linkInput" class="meeting-link-input"
          :value="meetingLink" :aria-label="t('button.copyMeetingLink')" readonly />
      </template>
      <template v-else>
        <h1>{{ t('message.meetingLinkMissingTitle') }}</h1>
        <p>{{ t('message.meetingLinkMissingHint') }}</p>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  meetingLink: { type: String, default: '' },
})
const { t } = useI18n()
const copying = ref(false)
const copyState = ref('')
const manualCopy = ref(false)
const linkInput = ref(null)
const copyMessage = computed(() => copyState.value ? t('message.' + copyState.value) : '')

const copyLink = async () => {
  if (copying.value || !props.meetingLink) return
  copying.value = true
  copyState.value = ''
  manualCopy.value = false
  try {
    await navigator.clipboard.writeText(props.meetingLink)
    copyState.value = 'meetingLinkCopied'
  } catch {
    // Some embedded browsers block clipboard access; allow manual copying.
    manualCopy.value = true
    copyState.value = 'meetingLinkCopyFallback'
    await nextTick()
    linkInput.value?.focus()
    linkInput.value?.select()
  } finally {
    copying.value = false
  }
}
</script>

<style scoped>
.wechat-browser-guide {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-height: 100dvh;
  position: relative;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: calc(env(safe-area-inset-top, 0px) + 140px) 28px 40px;
  background: rgba(20, 23, 29, 0.96);
  color: #fff;
  text-align: center;
}
.browser-guide-arrow {
  width: 130px;
  height: 120px;
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 6px);
  right: calc(env(safe-area-inset-right, 0px) + 8px);
  fill: none;
  stroke: currentColor;
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
  pointer-events: none;
  animation: browser-arrow-nudge 1.6s ease-in-out infinite;
}
.browser-guide-copy {
  width: 100%;
  max-width: 380px;
}
.browser-guide-menu {
  width: 68px;
  height: 42px;
  margin: 0 auto 24px;
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  letter-spacing: 4px;
  line-height: 1;
  text-indent: 4px;
}
.browser-guide-copy h1 {
  margin: 0;
  font-size: 24px;
  line-height: 1.4;
}
.browser-guide-copy p {
  margin: 16px 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 16px;
  line-height: 1.7;
}
.copy-link-button {
  margin-top: 24px;
  padding: 12px 22px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font: inherit;
  cursor: pointer;
}
.copy-link-button:disabled {
  opacity: 0.6;
  cursor: wait;
}
.meeting-link-input {
  box-sizing: border-box;
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  background: #fff;
  color: #222;
  font-size: 16px;
}
@keyframes browser-arrow-nudge {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-6px, 8px); }
}
@media (prefers-reduced-motion: reduce) {
  .browser-guide-arrow { animation: none; }
}
</style>
